<template>
  <div class="brands-page">
    <NavBar />

    <div class="brands-header">
      <div class="container">
        <h1 class="page-title">
          {{ $t('brands.allBrands') }}
        </h1>
        <p class="page-subtitle">
          {{ $t('brands.pageSubtitle') }}
        </p>
      </div>
    </div>

    <div class="brands-content">
      <div class="container">
        <!-- 品牌列表 -->
        <div class="brands-list">
          <van-list
            v-model:loading="loading"
            :finished="finished"
            :finished-text="$t('common.noMoreData')"
            @load="onLoad"
          >
            <div class="brands-grid">
              <BrandCard
                v-for="brand in brandList"
                :key="brand.id"
                :brand="brand"
                @click="handleBrandClick(brand)"
              />
            </div>
          </van-list>
        </div>

        <!-- 加载状态 -->
        <div
          v-if="loading && !brandList.length"
          class="loading-state"
        >
          <van-loading size="24px">
            {{ $t('common.loading') }}
          </van-loading>
        </div>

        <!-- 空状态 -->
        <div
          v-if="!loading && !brandList.length"
          class="empty-state"
        >
          <van-empty
            :description="$t('brands.noBrandsFound')"
            image="search"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import NavBar from '@/components/NavBar.vue'
import BrandCard from '@/components/brand/BrandCard.vue'
import { queryBrandList } from '@/api/brand'
import type { Brand } from '@/api/brand'
import type { PaginationParams } from '@/api/base'
import { useI18n } from 'vue-i18n'

export default {
  name: 'BrandsPage',
  components: {
    NavBar,
    BrandCard
  },
  setup () {
    const router = useRouter()
    const { t } = useI18n()
    const brandList = ref<Brand[]>([])
    const param = reactive<PaginationParams>({
      page: 1,
      size: 99
    })
    const scroll = reactive({
      loadMore: true,
      loadMoreText: t('common.loadMoreText'),
      loading: false,
      finished: false
    })
    function getBrandList (isLoadMore: boolean) {
      if (!isLoadMore) {
        brandList.value = []
        param.page = 1
        scroll.loadMore = true
        scroll.loadMoreText = t('common.loadMoreText')
      }
      scroll.loading = true
      queryBrandList(param).then(res => {
        brandList.value = brandList.value.concat(res.list)
        if (param.page < res.pages) {
          scroll.loadMore = true
          scroll.finished = false
          scroll.loadMoreText = t('common.loadMoreText')
        } else {
          scroll.loadMore = false
          scroll.finished = true
          scroll.loadMoreText = res.list.length ? '- THE END -' : ''
        }
        scroll.loading = false
      })
    }

    function onLoad () {
      ++param.page
      getBrandList(true)
    }
    function handleBrandClick (brand: Brand) {
      router.push({
        name: 'BrandDetail',
        params: {
          id: brand?.id
        }
      })
    }
    onMounted(() => {
      getBrandList(false)
    })
    return {
      brandList,
      ...toRefs(scroll),
      handleBrandClick,
      onLoad
    }
  }
}

</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.brands-page {
  min-height: 100vh;
  background-color: $light-gray;
}

.brands-header {
  background: linear-gradient(135deg, $primary-blue 0%, darken($primary-blue, 10%) 100%);
  color: $white;
  padding: $spacing-xl 0;
  text-align: center;
}

.page-title {
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-sm;
}

.page-subtitle {
  font-size: $font-size-lg;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

.brands-content {
  padding: $spacing-xl 0;
}

.filters-section {
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  margin-bottom: $spacing-lg;
  box-shadow: $card-shadow;
}

.search-box {
  margin-bottom: $spacing-md;
}

.brands-list {
  min-height: 400px;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: $spacing-lg;
}

.loading-state,
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

// 响应式设计
@media (max-width: $breakpoint-lg) {
  .brands-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: $spacing-md;
  }
}

@media (max-width: $breakpoint-md) {
  .brands-header {
    padding: $spacing-lg 0;
  }

  .page-title {
    font-size: $font-size-xl;
  }

  .page-subtitle {
    font-size: $font-size-md;
  }

  .brands-content {
    padding: $spacing-lg 0;
  }

  .brands-grid {
    grid-template-columns: 1fr;
    gap: $spacing-md;
  }

  .filters-section {
    padding: $spacing-md;
    margin-bottom: $spacing-md;
  }
}

@media (max-width: $breakpoint-sm) {
  .page-title {
    font-size: $font-size-lg;
  }

  .page-subtitle {
    font-size: $font-size-sm;
  }
}
</style>
